import React, { useEffect } from 'react';
import Spinner from '../layouts/Spinner';
import { connect } from 'react-redux';
import {
    getStockTransfers,
    sortTableByColumn,
    getSelectedRows,
    setModal,
    setStockTransferMovementFilter,
} from '../../redux/actions/stock_transfer/stock_transfer.actions';
import { formattedDate } from '../../Utils/Common';
import MUIDataTable from 'mui-datatables';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Show, useAccess } from 'react-redux-permission';
import { Link } from 'react-router-dom';
import {
    handleHover,
    setMUIPage,
    updateMUISetting,
} from '../../redux/actions/helper/helper.actions';
import TransferModal from './modals/transferModal';
import MultiTransferModal from './modals/multiTransferModal';
import StockTransferDateFilter from './StockTransfer.datefilter';
import StockTransferMovementFilter from './StockTransfer.movementfilter';

function StockTransferTable({
    stock_transfer: {
        stock_transfers,
        set_row_checked,
        pagination,
        loading,
        sort_order_name,
        sort_order_direction,
    },
    getStockTransfers,
    sortTableByColumn,
    getSelectedRows,
    handleHover,
    hoverIndex,
    updateMUISetting,
    setMUIPage,
    muiPage,
    setModal,
    setStockTransferMovementFilter,
}) {
    const { hasPermission } = useAccess();
    const canDelete = hasPermission('inv.inventory.stock_transfers.delete');
    const canview = hasPermission('inv.inventory.stock_transfers.view');

    useEffect(() => {
        getStockTransfers(muiPage);
    }, []);

    const getMuiTheme = () =>
        createMuiTheme({
            overrides: {
                MUIDataTable: {
                    root: {},
                    paper: {
                        boxShadow: 'none',
                    },
                },
                MUIDataTableBodyRow: {
                    root: {
                        '&:nth-child(odd)': {
                            backgroundColor: 'rgba(0,0,0,.05)',
                        },
                    },
                },
                MuiTableRow: {
                    hover: {
                        '&:hover': {
                            backgroundColor: '#bfcfe4 !important',
                        },
                    },
                },
                MUIDataTableBodyCell: {
                    root: {
                        '&:nth-child(2)': {
                            height: '61px',
                            width: '250px',
                        },
                        '&:nth-child(3)': {
                            width: '250px',
                        },
                        '&:nth-child(4)': {
                            width: '250px',
                        },
                        '&:nth-child(5)': {
                            width: '250px',
                        },
                        '&:nth-child(6)': {
                            width: '150px',
                        },
                    },
                },
            },
        });

    const columns = [
        {
            name: 'created_at',
            label: 'Date Created',
            options: {
                display: true,
                customBodyRender: (value) => {
                    return <span>{value ? value.substr(0, 10) : ''}</span>;
                },
                setCellProps: () => ({ className: 'cursor-pointer' }),
            },
        },
        {
            name: 'product_name',
            label: 'Product Name',
            options: {
                display: true,
                customBodyRender: (value, tableMeta) => {
                    const id =
                        stock_transfers[tableMeta.rowIndex]?.product_id;
                    return id ? (
                        <Link to={`/inventory/view/${id}`}>{value}</Link>
                    ) : (
                        <span>{value}</span>
                    );
                },
            },
        },
        {
            name: 'sku',
            label: 'SKU',
            options: {
                display: true,
                customBodyRender: (value) => <span>{value}</span>,
            },
        },
        {
            name: 'description',
            label: 'Description',
            options: {
                display: true,
                customBodyRender: (value) => <span>{value}</span>,
            },
        },
        {
            name: 'brand_name',
            label: 'Brand',
            options: {
                display: true,
                customBodyRender: (value, tableMeta) => {
                    const row = stock_transfers[tableMeta.rowIndex];
                    const brandId = row?.brand?._id;
                    return brandId ? (
                        <Link
                            to={`/inventory/products/brands/edit/${brandId}`}
                        >
                            {value}
                        </Link>
                    ) : (
                        <span>{value}</span>
                    );
                },
            },
        },
        {
            name: 'quantity',
            label: 'Qty',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <span>{value ?? 'N/A'}</span>,
                setCellProps: () => ({ className: 'cursor-pointer' }),
            },
        },
        {
            name: 'movement',
            label: 'Movement',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <span
                        className={`lbl-status lbl-${
                            value === 'in' ? 'success' : value === 'out' ? 'danger' : ''
                        }`}
                    >
                        {value ? value.toUpperCase() : 'N/A'}
                    </span>
                ),
                setCellProps: () => ({ className: 'cursor-pointer' }),
            },
        },
        {
            name: 'remarks',
            label: 'Remarks',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <span>{value || 'N/A'}</span>,
                setCellProps: () => ({ className: 'cursor-pointer' }),
            },
        },
    ];

    const handleMovementFilterChange = (filter) => {
        setStockTransferMovementFilter(filter);
    };

    const options = {
        serverSide: true,
        jumpToPage: true,
        filterType: 'dropdown',
        responsive: 'standard',
        search: false,
        filter: false,
        rowsPerPageOptions: [10, 20, 50, 100, 200],
        rowsPerPage: pagination.itemsCountPerPage,
        count: pagination.totalItemsCount,
        page: pagination.activePage - 1,
        sortOrder: {
            name: sort_order_name,
            direction: sort_order_direction,
        },
        downloadOptions: {
            filename: `stock_transfers_${formattedDate()}.csv`,
        },
        textLabels: {
            body: {
                noMatch: loading ? (
                    <Spinner />
                ) : (
                    'Sorry, there is no matching data to display'
                ),
            },
        },
        onChangeRowsPerPage: (numberOfRows) => {
            getStockTransfers(null, numberOfRows);
            updateMUISetting('rows', 'stock_transfer', numberOfRows);
        },
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    getStockTransfers(
                        tableState.page + 1,
                        pagination.itemsCountPerPage
                    );
                    setMUIPage(tableState.page + 1);
                    break;
                case 'sort':
                    sortTableByColumn(
                        tableState.sortOrder.name,
                        tableState.sortOrder.direction
                    );
                    break;
                case 'rowSelectionChange':
                    getSelectedRows(
                        tableState.selectedRows.data,
                        tableState.data
                    );
                    break;
                case 'propsUpdate':
                    if (set_row_checked) {
                        tableState.selectedRows['data'] = [];
                        tableState.selectedRows['lookup'] = {};
                        tableState.selectedRows['showResponsive'] = false;
                    } else {
                        return tableState;
                    }
                    break;
                case 'viewColumnsChange':
                    updateMUISetting(
                        'columns',
                        'stock_transfer',
                        null,
                        tableState.columns
                    );
                    break;
                default:
                    break;
            }
        },
        setRowProps: (data, dataIndex, rowIndex) => ({
            onMouseEnter: () => handleHover('in', rowIndex),
            onMouseLeave: () => handleHover('out', rowIndex),
        }),
    };

    if (!canDelete) {
        options.selectableRows = false;
    }

    const _columns = updateMUISetting(
        'view-columns',
        'stock_transfer',
        null,
        columns
    );

    return (
        <>
            <Show when="inv.inventory.stock-transfer.view">
                <section className="filter-container">
                    <div className="w-100">
                        <StockTransferDateFilter />
                    </div>
                    <div className="w-100">
                        <StockTransferMovementFilter
                            onFilterChange={handleMovementFilterChange}
                        />
                    </div>
                </section>
                <MuiThemeProvider theme={getMuiTheme()}>
                    <MUIDataTable
                        title=""
                        data={stock_transfers}
                        columns={_columns}
                        options={options}
                    />
                </MuiThemeProvider>
            </Show>
            <TransferModal
                className="modal-centered"
                onHide={() => setModal('stock_transfer_modal', null)}
            />
            <MultiTransferModal
                className="modal-centered"
                onHide={() => setModal('multi_stock_transfer_modal', null)}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    stock_transfer: state.stock_transfer,
    hoverIndex: state.helper.hoverIndex,
    muiPage: state.helper.muiPage,
});

export default connect(mapStateToProps, {
    getStockTransfers,
    sortTableByColumn,
    getSelectedRows,
    handleHover,
    updateMUISetting,
    setMUIPage,
    setModal,
    setStockTransferMovementFilter,
})(StockTransferTable);
